// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyWarranty } from "@/types";
import Icon from "@/UI/atoms/Icon";
import NumberInput from "@/UI/inputs/NumberInput";
import PhoneInput from "@/UI/inputs/PhoneInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyWarrantyFormDetails = {
    unitId: string;
    /** The whole list, because the array is patched as a whole — see below. */
    warranties: TyWarranty[];
    /** The entry being edited, or `null` to add one. */
    warranty?: TyWarranty | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * One warranty the unit carries. A **template**, not a live guarantee: it has a duration and no dates,
 * because the clock starts at the sale — selling the unit is what writes the real record.
 *
 * **Centred, not a side panel.** It is five fields; a full-height panel for five fields leaves two
 * thirds of itself empty, and a side panel is the shape that says "there is a lot of this".
 *
 * The whole `warranties` array is sent on every save. It is an embedded list of three or four entries
 * read with its unit; giving it its own endpoints would be three routes to save one round trip.
 */
export default function WarrantyFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { unitId, warranties, warranty, onDone } = details as TyWarrantyFormDetails;

    const [type, setType] = useState<string>(warranty?.type ?? "structure");
    const [providerName, setProviderName] = useState(warranty?.provider?.name ?? "");
    const [providerPhone, setProviderPhone] = useState<string | undefined>(warranty?.provider?.phone ?? "");
    const [months, setMonths] = useState<number | null>(warranty?.months ?? null);
    const [notes, setNotes] = useState(warranty?.notes ?? "");
    /** The form's own refusal, kept apart from the API's so one does not clear the other. */
    const [fault, setFault] = useState<string | null>(null);

    const { loading, error, succeededAt, invoke } = useFetch(`/properties/units/${unitId}`, { method: "PATCH" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        // The one required field, checked here because a template with no duration is a sale that
        // cannot write its records — see §5 of `ai.plans/warranty-notification-models.md`.
        if (!months || months <= 0) {
            setFault(t.warranties.monthsRequired);
            return;
        }
        setFault(null);

        const entry = {
            type,
            // The picker hands back `+966 5…` with spaces in it; the API wants E.164.
            provider: { name: providerName.trim() || null, phone: (providerPhone ?? "").replace(/\s/g, "") || null },
            months,
            notes: notes.trim() || null,
        };
        const next = warranty ? warranties.map((item) => (item._id === warranty._id ? { ...item, ...entry } : item)) : [...warranties, entry];

        invoke({ method: "PATCH", data: { warranties: next } });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{warranty ? t.properties.editWarranty : t.properties.addWarranty}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <SelectInput
                    value={type}
                    onChange={setType}
                    options={constants.warrantyTypes.map((item) => ({ value: item, label: t.properties.warrantyTypes[item] }))}
                    label={t.properties.fields.type}
                    disabled={loading}
                />

                {/* Who stands behind it and for how long — one question, so one row. The duration is
                    two digits and takes two digits' width; the name takes the rest.
                    The months are optional here so a shell can be saved, but a sale cannot write a
                    real record without them. */}
                <div className="side-form__row side-form__row--split">
                    <TextInput value={providerName} onChange={setProviderName} label={t.properties.fields.provider} disabled={loading} />
                    <NumberInput
                        value={months}
                        onChange={setMonths}
                        label={t.properties.fields.months}
                        suffix={t.properties.months}
                        min={1}
                        disabled={loading}
                    />
                </div>

                {/* The app's one phone field, flag and fixed `+966` included — a `tel` box here meant
                    the guarantor's number was the only number in the product stored however it was typed. */}
                <PhoneInput value={providerPhone} onChange={setProviderPhone} label={t.properties.fields.providerPhone} disabled={loading} />

                <TextInput value={notes} onChange={setNotes} label={t.properties.fields.notes} multiline rows={5} disabled={loading} />

                {(fault || error) && <p className="side-form__error">{fault ?? error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
